import {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Table, TableSortDirection} from '../../../../../../../../../components/table/table';
import {Attachment} from '../../../../../../../../../backend/interfaces/attachment';
import {UsersInfoCache} from '../../../../../../../../../hooks/use-users-info-cache';
import {translations} from '../../../../../../../../../i18n/translations';
import {SeniorAttachmentsTableItem} from './senior-attachments-table-item';

type SeniorAttachmentsListProps = {
  attachments?: Attachment[];
  authors: UsersInfoCache;
  canWrite?: boolean;
  loading?: boolean;
  onAttachmentEditClick: (attachment: Attachment) => void;
  onAttachmentDeleteClick: (attachment: Attachment) => void;
  onAttachmentDownloadClick: (attachment: Attachment) => void;
};

type TableColumnId = 'name' | 'description' | 'author' | 'date' | 'size' | 'actions';

export function SeniorAttachmentsTable(props: SeniorAttachmentsListProps) {
  const {t} = useTranslation();
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('desc');
  const [sortBy, setSortBy] = useState<TableColumnId>('date');

  const handleColumnSort = (columnId: TableColumnId) => {
    const isAsc = sortBy === columnId && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortBy(columnId);
  };

  const sortedAttachments = useMemo(() => {
    return props.attachments?.slice().sort((a, b) => {
      if (sortBy === 'name') {
        if (!a.name && !b.name) return 0; // No sort
        if (!a.name) return 1; // b comes first
        if (!b.name) return -1; // a comes first

        return sortDirection === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }

      if (sortBy === 'description') {
        if (!a.description && !b.description) return 0; // No sort
        if (!a.description) return 1; // b comes first
        if (!b.description) return -1; // a comes first

        return sortDirection === 'asc'
          ? a.description.localeCompare(b.description)
          : b.description.localeCompare(a.description);
      }

      if (sortBy === 'author') {
        if (!a.authorUserId || !b.authorUserId) return 0; // No sort
        const authorA = props.authors[a.authorUserId];
        const authorB = props.authors[b.authorUserId];
        if (!authorA || !authorB) return 0; // No sort
        if (!authorA) return 1; // b comes first
        if (!authorB) return -1; // a comes first

        return sortDirection === 'asc'
          ? authorA.name.localeCompare(authorB.name)
          : authorB.name.localeCompare(authorA.name);
      }

      if (sortBy === 'date') {
        if (!a.createdAt && !b.createdAt) return 0; // No sort
        if (!a.createdAt) return 1; // b comes first
        if (!b.createdAt) return -1; // a comes first

        return sortDirection === 'asc'
          ? new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }

      if (sortBy === 'size') {
        if (!a.sizeInMb && !b.sizeInMb) return 0; // No sort
        if (!a.sizeInMb) return 1; // b comes first
        if (!b.sizeInMb) return -1; // a comes first

        return sortDirection === 'asc' ? a.sizeInMb - b.sizeInMb : b.sizeInMb - a.sizeInMb;
      }

      return 0; // Default case, no sorting
    });
  }, [props.attachments, props.authors, sortBy, sortDirection]);

  return (
    <Table loading={props.loading}>
      <Table.CommonHeader<TableColumnId>
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSortClick={handleColumnSort}
      >
        {/* ---- NAME ---- */}
        <Table.HeaderSortCell
          id="name"
          label={t(translations.user_attachments.list.headers.name)}
        />

        {/* ---- DESCRIPTION ---- */}
        <Table.HeaderSortCell
          id="description"
          label={t(translations.user_attachments.list.headers.description)}
        />

        {/* ---- AUTHOR ---- */}
        <Table.HeaderSortCell
          id="author"
          label={t(translations.user_attachments.list.headers.author)}
        />

        {/* ---- DATE ---- */}
        <Table.HeaderSortCell
          id="date"
          label={t(translations.user_attachments.list.headers.date)}
        />

        {/* ---- SIZE ---- */}
        <Table.HeaderSortCell
          id="size"
          label={t(translations.user_attachments.list.headers.size)}
        />

        {/* ---- ACTIONS ---- */}
        <Table.Cell />
      </Table.CommonHeader>

      <Table.CommonBody loading={props.loading}>
        {sortedAttachments?.map((item) => (
          <SeniorAttachmentsTableItem
            key={item.id}
            item={item}
            author={props.authors[item.authorUserId ?? -1]}
            onEditClick={props.onAttachmentEditClick}
            onDeleteClick={props.onAttachmentDeleteClick}
            onDownloadClick={props.onAttachmentDownloadClick}
            canWrite={props.canWrite}
          />
        ))}
      </Table.CommonBody>
    </Table>
  );
}
